<script setup lang="ts">
import {
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  SidebarProvider,
  SidebarMenu,
  SidebarMenuItem,
  SidebarGroup,
  SidebarGroupLabel,
  SidebarMenuSubItem,
  SidebarMenuButton,
  SidebarInset,
  SidebarRail,
  SidebarTrigger,
  SidebarMenuSub,
  SidebarMenuAction,
  SidebarMenuSubButton,
} from './sidebar';
import {
  DropdownMenu,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuContent,
  DropdownMenuTrigger,
  DropdownMenuLabel,
  DropdownMenuGroup,
} from './dropdown-menu';
import { Collapsible, CollapsibleTrigger, CollapsibleContent } from './collapsible';
import { Breadcrumb, BreadcrumbItem, BreadcrumbLink, BreadcrumbSeparator, BreadcrumbList } from './breadcrumb';
import { Avatar, AvatarImage, AvatarFallback } from './avatar';
import { Separator } from './separator';
import { computed, ref, watch, onMounted } from 'vue';
import { email, signOut } from './firebase';
import { type RouterLinkProps, RouterView, RouterLink, useRouter } from 'vue-router';
import { Popover, PopoverContent, PopoverTrigger } from './popover';
import { cn } from './ui-utils';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from './command';
import { you } from './users';
import SidebarHoverHandler from './SidebarHoverHandler.vue';
import toolkitLogo from '../tri-frontend/assets/toolkit-logo-only.svg';
import type { NavMenuSection } from './navigation.ts';

import type { FacilityBrief } from '../cross-app-shared/types/common';
import { facilitiesList } from './facilities';
import { Badge } from './badge';

defineProps<{
  navMenu: NavMenuSection[];
  breadcrumbs: { id: number; title: string; to: RouterLinkProps['to'] }[];
  facilityFromRouteOrLastVisited: FacilityBrief | null;
  includeWelcome: boolean;
  isPrint?: boolean;
  padding: boolean;
  compactHeader: boolean;
}>();

defineEmits<{
  facilityUpdate: [FacilityBrief];
}>();

const router = useRouter();

const userMenu: {
  id: string;
  items: ({ title: string; icon: string } & ({ action: () => unknown } | { to: RouterLinkProps['to'] }))[];
}[] = [
  {
    id: '1',
    items: [
      {
        title: 'Log out',
        icon: 'fa-left-from-bracket',
        action: () => signOut(),
      },
    ],
  },
];

const facilityPickerOpen = ref(false);

// Collapsible sidebar sections state
const expandedSection = ref<'workflow' | 'help' | 'admin' | null>('workflow');

function handleSectionToggle(sectionId: 'workflow' | 'help' | 'admin', open: boolean) {
  // If open is true, expand the section; if false, collapse it
  expandedSection.value = open ? sectionId : null;
}

// Automatically determine which section should be expanded based on current route
const currentlyActiveSection = computed(() => {
  const currentRouteName = router.currentRoute.value.matched.filter((r) => !!r.name)[0]?.name;

  // If on Welcome page, don't expand any sections
  if (currentRouteName === 'Welcome') {
    return null;
  }

  const adminRoutes = [
    'Overall Status',
    'Anomalies',
    'Mass Actions',
    'Release Profiles',
    'Admin Release Comparison',
    'Chemicals',
    'Receptors',
    'Facilities',
    'Users',
    'HazWaste Training Management',
  ];
  const helpRoutes = ['TRI Knowledge', 'App Help'];

  if (adminRoutes.includes(currentRouteName as string)) {
    return 'admin';
  }
  if (helpRoutes.includes(currentRouteName as string)) {
    return 'help';
  }
  return 'workflow';
});

// Update expanded section when route changes (only if no section is currently expanded or if section should auto-expand)
watch(
  currentlyActiveSection,
  (newSection) => {
    // Only auto-expand if no section is currently expanded, or if we need to show the active section
    if (expandedSection.value === null || newSection !== null) {
      expandedSection.value = newSection;
    }
  },
  { immediate: true },
);

const userName = computed(() => you.value.name);
const userAvatarFallback = computed(() => you.value.email.slice(0, 2).toUpperCase());
const userAvatar = '';
const userEmail = computed(() => email);

// Track sidebar expansion for trigger button styling
const sidebarExpanded = ref(false);

// Handle sidebar state changes from SidebarHoverHandler
function handleSidebarStateChange(state: 'expanded' | 'collapsed') {
  sidebarExpanded.value = state === 'collapsed';
}

// Close sidebar on mobile when navigating
onMounted(() => {
  router.afterEach(() => {
    // Close mobile sidebar after navigation on small screens
    if (window.innerWidth < 768) {
      // Find and click the sidebar trigger button to close it
      const triggerButton = document.querySelector('[data-sidebar="trigger"]') as HTMLElement;
      if (triggerButton) {
        // Check if sidebar is open by looking for the mobile overlay
        const sidebar = document.querySelector('[data-sidebar="sidebar"]') as HTMLElement;
        if (sidebar && sidebar.getAttribute('data-state') === 'open') {
          triggerButton.click();
        }
      }
    }
  });
});
</script>

<template>
  <SidebarProvider :class="{ 'sidebar-expanded': sidebarExpanded }">
    <SidebarHoverHandler @sidebarStateChange="handleSidebarStateChange">
      <SidebarHeader>
        <SidebarMenu>
          <SidebarMenuItem>
            <Popover v-model:open="facilityPickerOpen">
              <PopoverTrigger asChild>
                <SidebarMenuButton
                  size="lg"
                  class="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
                >
                  <div
                    class="bg-sidebar-primary text-sidebar-primary-foreground bg-brand-blue flex aspect-square size-8 items-center justify-center rounded-lg"
                  >
                    <img :src="toolkitLogo" alt="Toolkit" class="h-5 w-5 object-contain" />
                  </div>
                  <div class="grid flex-1 text-left text-sm leading-tight">
                    <span class="truncate font-semibold">{{
                      facilityFromRouteOrLastVisited?.name ?? 'No Facility'
                    }}</span>
                    <span class="truncate text-xs">{{ facilityFromRouteOrLastVisited?.groupName ?? 'No Group' }}</span>
                  </div>
                  <i class="fa-light fa-angles-up-down ml-auto"></i>
                </SidebarMenuButton>
              </PopoverTrigger>
              <PopoverContent class="w-(--reka-popover-trigger-width) p-0">
                <Command
                  v-if="facilityFromRouteOrLastVisited"
                  :modelValue="facilityFromRouteOrLastVisited"
                  @update:modelValue="$emit('facilityUpdate', $event as FacilityBrief)"
                >
                  <CommandInput placeholder="Search facilities..." />
                  <CommandEmpty>No facility found.</CommandEmpty>
                  <CommandList>
                    <CommandGroup>
                      <CommandItem
                        v-for="facility of facilitiesList"
                        :key="facility.name"
                        :value="facility"
                        @select="facilityPickerOpen = false"
                        class="cursor-pointer"
                      >
                        <i
                          class="fa-light fa-check"
                          :class="
                            cn(
                              'mr-2 h-4 w-4',
                              facilityFromRouteOrLastVisited?.facilityID === facility.facilityID
                                ? 'opacity-100'
                                : 'opacity-0',
                            )
                          "
                        ></i>
                        {{ facility.groupName }} -
                        {{ facility.name }}
                      </CommandItem>
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
          </SidebarMenuItem>
          <slot name="afterFacilityPicker" />
        </SidebarMenu>
      </SidebarHeader>

      <SidebarContent>
        <!-- Welcome Link at top level -->
        <div v-if="includeWelcome">
          <div
            class="text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground flex cursor-pointer items-center justify-between rounded-md px-3 py-2 text-sm font-semibold transition-colors"
            :class="$route.name === 'Welcome' ? 'bg-sidebar-accent text-sidebar-accent-foreground' : 'text-white'"
            @click="$router.push({ name: 'Welcome' })"
          >
            <div class="flex items-center gap-2">
              <i class="fa-solid fa-home text-lg"></i>
              <span class="group-data-[collapsible=icon]:hidden">Welcome</span>
            </div>
          </div>
        </div>

        <div v-for="section of navMenu" :key="section.id" class="mb-1">
          <!-- Section Header (Collapsible) -->
          <Collapsible
            :open="expandedSection === section.id"
            @update:open="(open) => handleSectionToggle(section.id, open)"
            class="group/section"
          >
            <CollapsibleTrigger asChild>
              <div
                class="text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground flex cursor-pointer items-center justify-between rounded-md px-3 py-2 text-sm font-semibold text-white transition-colors"
              >
                <div class="flex items-center gap-2">
                  <i
                    class="fa-light text-lg"
                    :class="{
                      'fa-list-check': section.id === 'workflow',
                      'fa-question-circle': section.id === 'help',
                      'fa-gear': section.id === 'admin',
                    }"
                  ></i>
                  <span class="group-data-[collapsible=icon]:hidden">{{ section.title }}</span>
                </div>
                <i
                  class="fa-light fa-chevron-right transition-transform duration-200 group-data-[collapsible=icon]:hidden group-data-[state=open]/section:rotate-90"
                ></i>
              </div>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <!-- Section Groups -->
              <SidebarGroup v-for="group of section.groups" :key="group.title" class="mt-2">
                <SidebarGroupLabel>{{ group.title }}</SidebarGroupLabel>
                <SidebarMenu v-for="child of group.children" :key="child.title">
                  <Collapsible
                    v-if="child.type === 'collapsible'"
                    asChild
                    :defaultOpen="child.isActive"
                    class="group/collapsible"
                  >
                    <SidebarMenuItem>
                      <CollapsibleTrigger asChild>
                        <SidebarMenuButton :tooltip="child.title">
                          <div class="w-4">
                            <i class="fa-light fa-fw" :class="child.icon"></i>
                          </div>
                          <span>{{ child.title }}</span>
                          <i
                            class="fa-light fa-chevron-right ml-auto transition-transform duration-200 group-data-[state=open]/collapsible:rotate-90"
                          ></i>
                        </SidebarMenuButton>
                      </CollapsibleTrigger>
                      <CollapsibleContent>
                        <SidebarMenuSub>
                          <SidebarMenuSubItem v-for="page of child.pages" :key="page.title">
                            <SidebarMenuSubButton :as="RouterLink" :to="page.to" :isActive="page.isActive">
                              <div class="w-4">
                                <i class="fa-light fa-fw" :class="page.icon"></i>
                              </div>
                              {{ page.title }}
                            </SidebarMenuSubButton>
                          </SidebarMenuSubItem>
                        </SidebarMenuSub>
                      </CollapsibleContent>
                    </SidebarMenuItem>
                  </Collapsible>

                  <SidebarMenuItem v-else>
                    <SidebarMenuButton :as="RouterLink" :to="child.to" :isActive="child.isActive">
                      <div class="w-4">
                        <img
                          v-if="child.customIcon"
                          :src="child.customIcon"
                          :alt="child.title"
                          class="h-4 w-4 object-contain"
                        />
                        <i v-else class="fa-light fa-fw" :class="child.icon"></i>
                      </div>
                      {{ child.title }}
                      <Badge v-if="child.badge" :variant="child.badge.variant">{{ child.badge.label }}</Badge>
                    </SidebarMenuButton>
                    <DropdownMenu v-if="child.actions">
                      <DropdownMenuTrigger asChild>
                        <SidebarMenuAction showOnHover>
                          <i class="fa-light fa-ellipsis"></i>
                          <span class="sr-only">More</span>
                        </SidebarMenuAction>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent class="w-48 rounded-lg" side="bottom" align="end">
                        <DropdownMenuItem
                          v-for="action of child.actions"
                          :key="action.title"
                          @click="action.action()"
                          class="cursor-pointer"
                        >
                          <i class="fa-light fa-fw" :class="action.icon"></i>
                          <span>{{ action.title }}</span>
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </SidebarMenuItem>
                </SidebarMenu>
              </SidebarGroup>
            </CollapsibleContent>
          </Collapsible>
        </div>
      </SidebarContent>
      <SidebarFooter>
        <SidebarMenu>
          <SidebarMenuItem>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <SidebarMenuButton
                  size="lg"
                  class="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
                >
                  <Avatar class="bg-brand-green h-8 w-8 rounded-lg text-white">
                    <AvatarImage v-if="userAvatar" :src="userAvatar" :alt="userName" />
                    <AvatarFallback class="rounded-lg font-bold"> {{ userAvatarFallback }} </AvatarFallback>
                  </Avatar>
                  <div class="grid flex-1 text-left text-sm leading-tight">
                    <span class="truncate font-semibold">{{ userName }}</span>
                    <span class="truncate text-xs">{{ userEmail }}</span>
                  </div>
                  <i class="fa-light fa-angles-up-down"></i>
                </SidebarMenuButton>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                class="w-(--reka-dropdown-menu-trigger-width) min-w-56 rounded-lg"
                side="bottom"
                align="end"
                :sideOffset="4"
              >
                <DropdownMenuLabel class="p-0 font-normal">
                  <div class="flex items-center gap-2 px-1 py-1.5 text-left text-sm">
                    <Avatar class="bg-brand-green h-8 w-8 rounded-lg text-white">
                      <AvatarImage v-if="userAvatar" :src="userAvatar" :alt="userName" />
                      <AvatarFallback class="rounded-lg font-bold">
                        {{ userAvatarFallback }}
                      </AvatarFallback>
                    </Avatar>
                    <div class="grid flex-1 text-left text-sm leading-tight">
                      <span class="truncate font-semibold">{{ userName }}</span>
                      <span class="truncate text-xs">{{ userEmail }}</span>
                    </div>
                  </div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <template v-for="(group, index) of userMenu" :key="group.id">
                  <DropdownMenuGroup>
                    <template v-for="item of group.items" :key="item.title">
                      <DropdownMenuItem v-if="'action' in item" @click="item.action()" class="cursor-pointer">
                        <i class="fa-light fa-fw" :class="item.icon"></i>
                        {{ item.title }}
                      </DropdownMenuItem>
                      <DropdownMenuItem v-else :as="RouterLink" :to="item.to" class="cursor-pointer">
                        <i class="fa-light fa-fw" :class="item.icon"></i>
                        {{ item.title }}
                      </DropdownMenuItem>
                    </template>
                  </DropdownMenuGroup>
                  <DropdownMenuSeparator v-if="index < userMenu.length - 1" />
                </template>
              </DropdownMenuContent>
            </DropdownMenu>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarFooter>
      <SidebarRail />
    </SidebarHoverHandler>
    <SidebarInset :class="isPrint ? 'block' : 'grid h-svh grid-rows-[auto_auto_1fr] overflow-hidden'">
      <header
        v-if="!isPrint"
        :class="[
          'relative flex shrink-0 items-center gap-2 transition-[width,height] ease-linear',
          compactHeader
            ? 'h-10 group-has-data-[collapsible=icon]/sidebar-wrapper:h-10'
            : 'h-16 group-has-data-[collapsible=icon]/sidebar-wrapper:h-12',
        ]"
      >
        <div class="flex items-center gap-2 px-4">
          <SidebarTrigger :class="{ 'trigger-active': sidebarExpanded, '-ml-1': true }" />
          <Separator orientation="vertical" class="mr-2 h-4" />
          <Breadcrumb>
            <BreadcrumbList>
              <template v-for="(breadcrumb, index) in breadcrumbs" :key="breadcrumb.id">
                <BreadcrumbItem class="hidden md:block">
                  <BreadcrumbLink :as="RouterLink" :to="breadcrumb.to">{{ breadcrumb.title }}</BreadcrumbLink>
                </BreadcrumbItem>
                <BreadcrumbSeparator v-if="index < breadcrumbs.length - 1" class="hidden md:block" />
              </template>
            </BreadcrumbList>
          </Breadcrumb>
        </div>

        <slot name="afterBreadcrumbs" />
      </header>
      <slot name="subHeader" />
      <div
        :class="
          isPrint
            ? 'px-1 py-4 pt-0 md:px-4'
            : padding
              ? 'max-h-full overflow-y-auto px-1 py-4 pt-0 md:px-4'
              : 'h-full overflow-hidden'
        "
      >
        <RouterView />
      </div>
    </SidebarInset>
  </SidebarProvider>
</template>
<style scoped>
.brand-stripes {
  background: linear-gradient(
    to right,
    #2a38c6 0%,
    /* Blue */ #2a38c6 25%,
    #e8592a 25%,
    /* Orange */ #e8592a 50%,
    #f4c12f 50%,
    /* Yellow */ #f4c12f 75%,
    #3fa76f 75%,
    /* Green */ #3fa76f 100%
  );
}

.gradient-underline {
  display: inline;
  background-image: linear-gradient(
    to right,
    #2a38c6,
    /* Blue */ #e8592a,
    /* Orange */ #f4c12f,
    /* Yellow */ #3fa76f /* Green */
  );
  background-repeat: no-repeat;
  background-size: 100% 2px; /* Full width, 2px height */
  background-position: 0 100%; /* Position it at the bottom */
  padding-bottom: 2px; /* Prevent text from overlapping the underline */
}

/* Custom scrollbar styling with brand-blue color */
::-webkit-scrollbar {
  width: 12px;
  height: 12px;
}

::-webkit-scrollbar-track {
  background: hsl(237, 30%, 24%);
  border-radius: 6px;
}

::-webkit-scrollbar-thumb {
  background: #081848; /* brand-blue color */
  border-radius: 6px;
  border: 2px solid #f1f1f1;
}

::-webkit-scrollbar-thumb:hover {
  background: #0a1d5a; /* slightly lighter shade for hover */
}

::-webkit-scrollbar-corner {
  background: #f1f1f1;
}

[data-sidebar='content'] {
  scrollbar-width: thin;
  scrollbar-color: #081848 rgb(59, 81, 127); /* thumb color, track color */
}

/* Ask button styling - using SVG structure like ProgressTracker */
/* Ensure Ask button maintains position when dropdown opens */
.ask-dropdown-wrapper {
  position: relative;
  z-index: 20;
}

/* Prevent any layout shifts when dropdown is open */
.ask-dropdown-wrapper [data-state='open'] {
  position: relative;
}

/* Ensure sidebar takes full viewport height and proper transitions */
:deep([data-sidebar='sidebar']) {
  height: 100vh;
  transition: width 0.25s ease-in-out;
}

/* Make sidebar content scrollable */
:deep([data-sidebar='content']) {
  flex: 1;
  overflow-y: auto;
}

/* Trigger button active state */
.trigger-active {
  background-color: #081848 !important; /* brand-blue color */
  color: white !important;
  transition: all 0.2s ease-in-out;
}

.trigger-active:hover {
  background-color: #0a1d5a !important; /* slightly lighter shade for hover */
}
</style>
